import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfigProvider, Tour } from "antd";
import { GuideContext } from "@/context/GuideContext";
import { useAuth } from "@/context/AuthContext";
import { anchorSelector, getTourForPath } from "@/guides/tours";
import {
  hasSeenTour,
  markTourSeen,
  getTourProgress,
  setTourProgress,
  clearTourProgress,
} from "@/guides/tourStorage";
import { fitPlacement } from "./placement";
import { isAuthoringEnabled } from "./authoring";
import "./TourRunner.css";

const POLL_INTERVAL_MS = 250;
const AUTO_START_TIMEOUT_MS = 8000;
const MANUAL_START_TIMEOUT_MS = 2000;
// Small pause after anchors appear so charts and tables finish laying out.
const SETTLE_DELAY_MS = 400;

// Instant scroll: antd measures the target right after scrolling, so a smooth
// scroll (Bootstrap sets `scroll-behavior: smooth` on :root) would leave the
// popover aligned to the pre-scroll position, possibly off-screen.
// Must be set per step: rc-tour gives every step a default
// `scrollIntoViewOptions`, so the Tour-level prop is never used.
const SCROLL_INTO_VIEW_OPTIONS = { block: "center", behavior: "instant" };

const TOUR_THEME = {
  token: {
    colorPrimary: "#0b6a8a",
    borderRadius: 10,
    fontFamily: "inherit",
  },
};

export const findAnchor = (anchor, previewTour = null) => {
  if (!anchor || typeof anchor !== "string") return null;

  // 1. Direct [data-tour="..."] match in live DOM (standard anchors)
  if (!anchor.startsWith("pending:")) {
    try {
      const directEl = document.querySelector(anchorSelector(anchor));
      if (directEl) return directEl;
    } catch {
      // Invalid selector string (e.g. unescaped quote) treated safely as missing
    }
  }

  // 2. Authoring preview fallback via selector hint in previewTour.anchorRequests
  if (isAuthoringEnabled() && previewTour?.anchorRequests) {
    const req = previewTour.anchorRequests.find((r) => r.id === anchor);
    if (req?.hint) {
      const hint = req.hint;

      // 2a. Try selector and verify text match
      if (hint.selector) {
        try {
          const el = document.querySelector(hint.selector);
          if (el) {
            const textMatched =
              !hint.text ||
              el.textContent.includes(hint.text) ||
              hint.text.includes(el.textContent.trim().slice(0, 30));
            if (textMatched) return el;
          }
        } catch {}
      }

      // 2b. Drift recovery: if selector drifted, locate by hint text
      if (hint.text && hint.text.length >= 3) {
        try {
          const candidates = Array.from(document.querySelectorAll(hint.tag || "*")).filter(
            (el) => el.textContent.includes(hint.text),
          );
          if (candidates.length > 0) {
            const innermost = candidates.sort((a, b) => a.textContent.length - b.textContent.length)[0];
            const container = innermost.closest(".card, .card-radius, [class*='panel']") || innermost;
            return container;
          }
        } catch {}
      }

      // 2c. Fallback to direct selector
      if (hint.selector) {
        try {
          const el = document.querySelector(hint.selector);
          if (el) return el;
        } catch {}
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("elelem-tour:preview-target-missing", {
            detail: { anchor, note: req.note },
          }),
        );
      }
    }
  }

  return null;
};

const requiredAnchorsReady = (tour, previewTour = null) =>
  tour.steps.every(
    (step) =>
      !step.anchor ||
      step.optional ||
      Boolean(findAnchor(step.anchor, previewTour)),
  );

/** Steps whose anchor is on screen right now (centred steps always qualify). */
const availableSteps = (tour, previewTour = null) =>
  tour.steps.filter(
    (step) => !step.anchor || Boolean(findAnchor(step.anchor, previewTour)),
  );

/**
 * Waits until every required anchor of the tour is in the DOM (pages load
 * data asynchronously), then resolves with the steps that can be shown.
 * On timeout it falls back to whatever is available.
 */
const waitForSteps = (tour, timeoutMs, signal, previewTour = null) =>
  new Promise((resolve) => {
    const startedAt = Date.now();
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      signal?.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      resolve([]);
    };

    if (signal?.aborted) {
      return resolve([]);
    }
    signal?.addEventListener("abort", onAbort);

    const check = () => {
      if (signal?.aborted) {
        cleanup();
        return resolve([]);
      }
      if (
        requiredAnchorsReady(tour, previewTour) ||
        Date.now() - startedAt >= timeoutMs
      ) {
        timeoutId = setTimeout(() => {
          cleanup();
          resolve(signal?.aborted ? [] : availableSteps(tour, previewTour));
        }, SETTLE_DELAY_MS);
        return;
      }
      timeoutId = setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
  });

/** Auto-start only when at least one step points at a real element. */
const hasAnchoredStep = (steps) => steps.some((step) => step.anchor);
/** A manual start may show centred steps alone (e.g. on a page state without anchors). */
const hasAnyStep = (steps) => steps.length > 0;

/**
 * Renders the tour configured for the current route (see src/guides/tours.js).
 * Mounted once in the authenticated layout; pages need no tour-specific code
 * beyond the `data-tour` anchors.
 */
const TourRunner = () => {
  // `search` is watched too: some pages switch views via query params (e.g. ?tab=).
  const { pathname, search } = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    tourRequest,
    previewTour,
    clearTourPreview,
    setGuideMode,
  } = useContext(GuideContext);
  const username = user?.username;

  const configuredTour = useMemo(() => getTourForPath(pathname), [pathname]);
  const tour = previewTour || configuredTour;
  const isPreview = Boolean(previewTour);
  const tourId = tour ? (tour.version ? `${tour.id}@${tour.version}` : tour.id) : null;

  const [activeSteps, setActiveSteps] = useState([]);
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(false);
  // Placement actually used per step index (config placement is a preference).
  const [placements, setPlacements] = useState({});
  const lastHandledRequest = useRef(tourRequest);
  const activeControllerRef = useRef(null);

  const start = useCallback(
    (timeoutMs, canShow) => {
      if (!tour) return;
      activeControllerRef.current?.abort();
      const controller = new AbortController();
      activeControllerRef.current = controller;
      waitForSteps(tour, timeoutMs, controller.signal, previewTour).then((steps) => {
        if (controller.signal.aborted || !canShow(steps)) return;
        setActiveSteps(steps);
        setPlacements({});

        let resumeIndex = 0;
        if (!isPreview && tourId) {
          const saved = getTourProgress(username, tourId);
          if (saved && typeof saved.index === "number") {
            if (saved.anchor) {
              const matchingIdx = steps.findIndex(
                (s) => (s.anchor || s.key || s.en?.title) === saved.anchor,
              );
              resumeIndex = matchingIdx !== -1
                ? matchingIdx
                : Math.min(saved.index, Math.max(0, steps.length - 1));
            } else {
              resumeIndex = Math.min(saved.index, Math.max(0, steps.length - 1));
            }
          }
        }

        setCurrent(resumeIndex);
        setOpen(true);
      });
    },
    [tour, previewTour, isPreview, tourId, username],
  );

  const handleStepChange = useCallback(
    (nextIndex) => {
      setCurrent(nextIndex);
      if (!isPreview && tourId && activeSteps[nextIndex]) {
        const step = activeSteps[nextIndex];
        setTourProgress(username, tourId, {
          index: nextIndex,
          anchor: step.anchor || step.key || step.en?.title || null,
        });
      }
    },
    [isPreview, tourId, activeSteps, username],
  );

  const handleRestart = useCallback(() => {
    setCurrent(0);
    if (!isPreview && tourId && activeSteps[0]) {
      const step = activeSteps[0];
      setTourProgress(username, tourId, {
        index: 0,
        anchor: step.anchor || step.key || step.en?.title || null,
      });
    }
  }, [isPreview, tourId, activeSteps, username]);

  // Close any running tour when the view changes; auto-start on first visit.
  useEffect(() => {
    setOpen(false);
    activeControllerRef.current?.abort();
    if (!tour || isPreview || !tour.autoStart || hasSeenTour(username, tourId))
      return undefined;
    start(AUTO_START_TIMEOUT_MS, hasAnchoredStep);
    return () => activeControllerRef.current?.abort();
  }, [tour, isPreview, search, username, tourId, start]);

  // Progressive reveal observer: when the page dynamically mounts or unmounts
  // sections (e.g. Optimize Content brief generation or conditional drawers),
  // dynamically sync activeSteps so newly revealed anchors appear in the tour.
  useEffect(() => {
    if (!open || !tour) return undefined;

    let debounceTimer;
    const syncSteps = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const nextSteps = availableSteps(tour, previewTour);
        setActiveSteps((prev) => {
          const prevKeys = prev
            .map((s) => s.anchor || s.key || s.en?.title || "")
            .join("|");
          const nextKeys = nextSteps
            .map((s) => s.anchor || s.key || s.en?.title || "")
            .join("|");
          if (prevKeys === nextKeys) return prev;

          // Reconcile current step index using the active step's identity
          setCurrent((prevCurrent) => {
            const activeStepBefore = prev[prevCurrent];
            if (activeStepBefore) {
              const activeId = activeStepBefore.anchor || activeStepBefore.key || activeStepBefore.en?.title;
              const nextIndex = nextSteps.findIndex(
                (s) => (s.anchor || s.key || s.en?.title) === activeId,
              );
              if (nextIndex !== -1) {
                return nextIndex;
              }
            }
            return Math.min(prevCurrent, Math.max(0, nextSteps.length - 1));
          });

          return nextSteps;
        });
      }, 150);
    };

    if (typeof MutationObserver === "undefined") return undefined;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length > 0 || m.removedNodes.length > 0) {
          syncSteps();
          break;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [open, tour, previewTour]);

  // Manual (re)start from the Guide button or preview request.
  useEffect(() => {
    if (tourRequest === lastHandledRequest.current) return undefined;
    lastHandledRequest.current = tourRequest;
    if (!tour) return undefined;
    start(MANUAL_START_TIMEOUT_MS, hasAnyStep);
    return () => activeControllerRef.current?.abort();
  }, [tourRequest, tour, start]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("elelem-tour-open");
    } else {
      document.body.classList.remove("elelem-tour-open");
    }
    return () => {
      document.body.classList.remove("elelem-tour-open");
    };
  }, [open]);

  // antd Tour only re-measures its highlight on window resize. Pages scroll
  // inside containers too, so re-sync once scrolling settles.
  useEffect(() => {
    if (!open) return undefined;
    let timer;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => window.dispatchEvent(new Event("resize")), 120);
    };
    document.addEventListener("scroll", onScroll, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  // Bring the current target into view, then choose a placement that fits.
  useLayoutEffect(() => {
    if (!open) return;
    const step = activeSteps[current];
    const element = step?.anchor && findAnchor(step.anchor, previewTour);
    if (!element) return;
    let rect = element.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    if (rect.bottom < 0 || rect.top > viewport.height) {
      element.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
      rect = element.getBoundingClientRect();
    }
    const placement = fitPlacement(rect, step.placement, viewport);
    setPlacements((prev) =>
      prev[current] === placement ? prev : { ...prev, [current]: placement },
    );
  }, [open, current, activeSteps, previewTour]);

  const handleClose = useCallback(() => {
    activeControllerRef.current?.abort();
    setOpen(false);
    setGuideMode?.(false);
    if (isPreview) {
      clearTourPreview();
    } else if (tourId) {
      markTourSeen(username, tourId);
    }
  }, [isPreview, clearTourPreview, tourId, username, setGuideMode]);

  const handleFinish = useCallback(() => {
    if (!isPreview && tourId) {
      clearTourProgress(username, tourId);
    }
    handleClose();
  }, [isPreview, tourId, username, handleClose]);

  const steps = useMemo(() => {
    const total = activeSteps.length;
    const lang =
      i18n?.resolvedLanguage?.startsWith("ja") ||
      i18n?.language?.startsWith("ja")
        ? "ja"
        : "en";
    return activeSteps.map((step, index) => {
      const isLast = index === total - 1;
      const copy = step[lang] || step.en || {};
      const title = copy.title ?? (step.key ? t(`${step.key}.title`) : "");
      const body = copy.body ?? (step.key ? t(`${step.key}.body`) : "");
      return {
        title,
        description: body,
        target: step.anchor ? () => findAnchor(step.anchor, previewTour) : null,
        placement: placements[index] || step.placement || "bottom",
        scrollIntoViewOptions: SCROLL_INTO_VIEW_OPTIONS,
        nextButtonProps: {
          children: isLast ? t("tour.done", "Done") : t("tour.next", "Next"),
          onClick: isLast ? handleFinish : undefined,
        },
        prevButtonProps: { children: t("tour.previous", "Back") },
      };
    });
  }, [activeSteps, placements, t, i18n?.resolvedLanguage, i18n?.language, previewTour, handleFinish]);

  if (!tour || steps.length === 0) return null;

  return (
    <ConfigProvider theme={TOUR_THEME}>
      <Tour
        open={open}
        current={current}
        onChange={handleStepChange}
        onClose={handleClose}
        onFinish={handleFinish}
        steps={steps}
        zIndex={1100}
        rootClassName="elelem-tour"
        scrollIntoViewOptions={SCROLL_INTO_VIEW_OPTIONS}
        indicatorsRender={(currentIndex, total) => (
          <span className="d-inline-flex align-items-center gap-2">
            <span className="elelem-tour-progress" data-testid="tour-progress">
              {currentIndex + 1} / {total}
            </span>
            {currentIndex > 0 && (
              <button
                type="button"
                className="elelem-tour-restart-btn"
                onClick={handleRestart}
                title={t("tour.restart", "Restart from beginning")}
                data-testid="tour-restart-btn"
              >
                <i className="bi bi-arrow-counterclockwise" />
                {t("tour.restart", "Restart")}
              </button>
            )}
          </span>
        )}
        closeIcon={
          <span
            className="elelem-tour-close"
            aria-label={t("tour.close", "Close tour")}
          >
            ×
          </span>
        }
      />
    </ConfigProvider>
  );
};

export default TourRunner;
